import React, { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Panel, Badge } from "@/components/Bits";
import { BarChart3, TrendingUp, TrendingDown, Calendar, RefreshCw, Activity, DollarSign, CheckCircle2, Layers } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ComposedChart,
} from "recharts";

const CYAN = "#22d3ee";
const EMERALD = "#10b981";
const ROSE = "#f43f5e";
const AMBER = "#f59e0b";
const VIOLET = "#a78bfa";
const PALETTE = ["#22d3ee", "#10b981", "#f59e0b", "#a78bfa", "#f43f5e", "#38bdf8", "#f97316", "#84cc16", "#ec4899", "#eab308", "#14b8a6", "#8b5cf6"];

const fmtIDR = (v) => "IDR " + Math.round(Number(v || 0)).toLocaleString();
const fmtIDRShort = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1e9) return (n / 1e9).toFixed(1) + "B";
  if (Math.abs(n) >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (Math.abs(n) >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return n.toLocaleString();
};
const fmtInt = (v) => Number(v || 0).toLocaleString();
const fmtPct = (v) => (Number(v || 0)).toFixed(2) + "%";

const RANGES = [
  { key: "7d", label: "7 days", days: 6 },
  { key: "14d", label: "14 days", days: 13 },
  { key: "30d", label: "30 days", days: 29 },
  { key: "90d", label: "90 days", days: 89 },
];

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export default function MerchantAnalytics() {
  const { user } = useAuth();
  const canView = (user?.permissions || []).includes("agent_reports.manage");
  const [dateFrom, setDateFrom] = useState(todayISO(-29));
  const [dateTo, setDateTo] = useState(todayISO(0));
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [serviceType, setServiceType] = useState("PAYIN"); // PAYIN | PAYOUT | ALL
  const [metric, setMetric] = useState("rate"); // rate | amount | successful | attempts
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/merchant-performance/merchants").then((r) => setMerchants(r.data)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = { date_from: dateFrom, date_to: dateTo, service_type: serviceType };
      if (selectedMerchants.length) params.merchants = selectedMerchants.join(",");
      const { data } = await api.get("/merchant-performance/analytics", { params });
      setData(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (canView) load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, selectedMerchants, serviceType]);

  const applyRange = (days) => {
    setDateTo(todayISO(0));
    setDateFrom(todayISO(-days));
  };

  // Build multi-line dataset: one point per date, one series per merchant
  const perMerchantChartData = useMemo(() => {
    if (!data) return { rows: [], keys: [] };
    const merchantsToShow = selectedMerchants.length ? selectedMerchants : Object.keys(data.per_merchant_series).slice(0, 10);
    const dateSet = new Set();
    Object.values(data.per_merchant_series).forEach((series) => series.forEach((r) => dateSet.add(r.date)));
    const dates = Array.from(dateSet).sort();
    const rows = dates.map((d) => {
      const row = { date: d };
      merchantsToShow.forEach((m) => {
        const rec = (data.per_merchant_series[m] || []).find((x) => x.date === d);
        row[m] = rec ? Number(rec[metric] || 0) : null;
      });
      return row;
    });
    return { rows, keys: merchantsToShow };
  }, [data, selectedMerchants, metric]);

  if (!canView) {
    return <div className="p-8 text-slate-400 text-sm" data-testid="merchant-analytics-forbidden">Butuh permission <span className="font-mono text-cyan">agent_reports.manage</span> untuk mengakses halaman ini.</div>;
  }

  return (
    <div className="p-6 space-y-4 animate-slide-up" data-testid="merchant-analytics-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Analytics</div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-cyan" /> Merchant Analytics
          </h1>
          <p className="text-xs text-slate-400 mt-1 max-w-3xl">
            Visualisasi performa merchant harian dari data yang di-upload di Agent Reports. Lihat trend naik/turun, breakdown success/failed/pending, dan top movers per periode.
          </p>
        </div>
        <button data-testid="refresh-btn" onClick={load} disabled={loading} className="border border-line hover:bg-surface2 px-3 py-2 rounded-sm text-xs uppercase tracking-wider flex items-center gap-1.5 disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-cyan" : ""}`} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <Panel title="Filters" testid="filters">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Service</label>
            <div className="flex gap-1" data-testid="service-type-tabs">
              {[
                { k: "PAYIN", l: "Payin" },
                { k: "PAYOUT", l: "Payout" },
                { k: "ALL", l: "Semua" },
              ].map((opt) => (
                <button
                  key={opt.k}
                  data-testid={`service-${opt.k.toLowerCase()}`}
                  onClick={() => setServiceType(opt.k)}
                  className={`text-[10px] uppercase tracking-wider border px-2.5 py-1 rounded-sm ${serviceType === opt.k ? "border-cyan bg-cyan/10 text-cyan" : "border-line hover:border-slate-500 text-slate-400"}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Quick range</label>
            <div className="flex gap-1">
              {RANGES.map((r) => (
                <button key={r.key} data-testid={`range-${r.key}`} onClick={() => applyRange(r.days)} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2 hover:border-cyan">{r.label}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">From</label>
            <input data-testid="date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-bg border border-line rounded-sm px-2 py-1 text-xs" />
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400">To</label>
            <input data-testid="date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-bg border border-line rounded-sm px-2 py-1 text-xs" />
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-1.5">Merchants ({selectedMerchants.length ? selectedMerchants.length : "all"} selected · will show top 10 if empty)</label>
          <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto border border-line rounded-sm p-2" data-testid="merchant-picker">
            {merchants.map((m) => {
              const active = selectedMerchants.includes(m);
              return (
                <button
                  key={m}
                  data-testid={`merch-${m}`}
                  onClick={() => setSelectedMerchants((s) => active ? s.filter((x) => x !== m) : [...s, m])}
                  className={`text-[10px] px-2 py-0.5 rounded-sm border transition-colors ${active ? "border-cyan bg-cyan/10 text-cyan" : "border-line hover:border-slate-500 text-slate-400"}`}
                >
                  {m}
                </button>
              );
            })}
          </div>
          {selectedMerchants.length > 0 && (
            <button onClick={() => setSelectedMerchants([])} className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-white mt-1" data-testid="clear-merchants">Clear selection</button>
          )}
        </div>
      </Panel>

      {err && <div className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm">{err}</div>}
      {loading && <div className="text-xs text-slate-400 flex items-center gap-2"><div className="w-3 h-3 border-2 border-cyan border-t-transparent rounded-full animate-spin" /> Loading analytics...</div>}

      {!loading && data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiCard icon={Activity} label="Total Attempts" value={fmtInt(data.summary.total_attempts)} color="cyan" testid="kpi-attempts" />
            <KpiCard icon={CheckCircle2} label="Total Successful" value={fmtInt(data.summary.total_successful)} color="emerald" testid="kpi-success" />
            <KpiCard icon={DollarSign} label="Total Amount" value={fmtIDR(data.summary.total_amount)} color="cyan" testid="kpi-amount" small />
            <KpiCard icon={Layers} label="Avg Rate" value={fmtPct(data.summary.avg_rate)} color={data.summary.avg_rate >= 90 ? "emerald" : data.summary.avg_rate >= 80 ? "amber" : "rose"} testid="kpi-rate" />
            <KpiCard icon={BarChart3} label="Merchants × Days" value={`${data.summary.merchant_count} × ${data.summary.date_count}`} color="slate" testid="kpi-scope" />
          </div>

          {/* Daily total trend (line + rate) */}
          <Panel title="Daily Total Transactions" testid="daily-trend-panel">
            {data.daily_totals.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={data.daily_totals} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2938" />
                  <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={fmtIDRShort} />
                  <YAxis yAxisId="right" orientation="right" stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={(v) => v + "%"} domain={[0, 100]} />
                  <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1e2938", fontSize: 12 }} formatter={(val, name) => name === "rate" ? [fmtPct(val), "Rate"] : [fmtInt(val), name]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="attempts" fill={CYAN} fillOpacity={0.35} name="Attempts" />
                  <Line yAxisId="left" type="monotone" dataKey="successful" stroke={EMERALD} strokeWidth={2} dot={{ r: 3 }} name="Successful" />
                  <Line yAxisId="right" type="monotone" dataKey="rate" stroke={AMBER} strokeWidth={2} dot={{ r: 3 }} name="Success Rate %" />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </Panel>

          {/* Stacked: Success / Failed / Pending */}
          <Panel title="Daily Breakdown (Success / Failed / Pending)" testid="stacked-panel">
            {data.daily_totals.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.daily_totals} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2938" />
                  <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#64748b" tick={{ fontSize: 11 }} tickFormatter={fmtIDRShort} />
                  <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1e2938", fontSize: 12 }} formatter={(val, name) => [fmtInt(val), name]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="successful" stackId="a" fill={EMERALD} name="Successful" />
                  <Bar dataKey="failed" stackId="a" fill={ROSE} name="Failed" />
                  <Bar dataKey="pending" stackId="a" fill={AMBER} name="Pending" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>

          {/* Per-merchant multi-line */}
          <Panel title="Per Merchant Trend" testid="per-merchant-panel">
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                Showing {perMerchantChartData.keys.length} merchant(s){!selectedMerchants.length && ` (top 10 by data — pilih di filter untuk fokus)`}
              </div>
              <div className="flex items-center gap-1">
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mr-1">Metric</label>
                {[
                  { k: "rate", l: "Rate %" },
                  { k: "amount", l: "Amount" },
                  { k: "successful", l: "Successful" },
                  { k: "attempts", l: "Attempts" },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    data-testid={`metric-${opt.k}`}
                    onClick={() => setMetric(opt.k)}
                    className={`text-[10px] uppercase tracking-wider border px-2 py-1 rounded-sm ${metric === opt.k ? "border-cyan bg-cyan/10 text-cyan" : "border-line hover:border-slate-500 text-slate-400"}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>
            {perMerchantChartData.rows.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={perMerchantChartData.rows} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2938" />
                  <XAxis dataKey="date" stroke="#64748b" tick={{ fontSize: 11 }} />
                  <YAxis
                    stroke="#64748b"
                    tick={{ fontSize: 11 }}
                    tickFormatter={metric === "rate" ? (v) => v + "%" : fmtIDRShort}
                    domain={metric === "rate" ? [0, 100] : ["auto", "auto"]}
                  />
                  <Tooltip
                    contentStyle={{ background: "#111827", border: "1px solid #1e2938", fontSize: 12 }}
                    formatter={(val) => metric === "rate" ? fmtPct(val) : metric === "amount" ? fmtIDR(val) : fmtInt(val)}
                  />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  {perMerchantChartData.keys.map((k, i) => (
                    <Line key={k} type="monotone" dataKey={k} stroke={PALETTE[i % PALETTE.length]} strokeWidth={2} dot={{ r: 2 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </Panel>

          {/* Top Movers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <MoversTable title="Top Movers ↑" icon={TrendingUp} rows={data.top_movers.up} accent="emerald" testid="movers-up" />
            <MoversTable title="Top Movers ↓" icon={TrendingDown} rows={data.top_movers.down} accent="rose" testid="movers-down" />
          </div>

          <div className="text-[10px] text-slate-500 text-center pt-2">
            <Calendar className="w-3 h-3 inline mr-1" /> Movers compare 2nd-half vs 1st-half of selected date range · Data dipisah per jenis layanan (Payin / Payout) — gunakan tab Service di filter.
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color, testid, title, small }) {
  const cmap = {
    cyan: "border-cyan/30 bg-cyan/5 text-cyan",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-400",
    rose: "border-rose-500/30 bg-rose-500/5 text-rose-400",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-400",
    slate: "border-line bg-surface2 text-white",
  };
  return (
    <div className={`border rounded-sm p-3 ${cmap[color] || cmap.slate}`} title={title}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="w-3.5 h-3.5 opacity-70" />
      </div>
      <div className={`${small ? "text-sm" : "text-xl"} font-bold font-mono mt-1 break-all leading-tight`} data-testid={testid}>{value}</div>
    </div>
  );
}

function EmptyChart() {
  return <div className="text-center text-slate-500 text-xs py-16">Belum ada data untuk periode ini. Upload CSV di <span className="font-mono text-cyan">Agent Reports · Daily Performance</span>.</div>;
}

function MoversTable({ title, icon: Icon, rows, accent, testid }) {
  const dirColor = accent === "emerald" ? "text-emerald-400" : "text-rose-400";
  const badgeColor = accent === "emerald" ? "success" : "error";
  return (
    <Panel title={<span className="flex items-center gap-2"><Icon className={`w-4 h-4 ${dirColor}`} /> {title}</span>} testid={testid}>
      {rows.length === 0 ? (
        <div className="text-center py-6 text-slate-500 text-xs">—</div>
      ) : (
        <div className="border border-line rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface2 border-b border-line">
              <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                <th className="text-left px-3 py-2 font-medium">Merchant</th>
                <th className="text-right px-3 py-2 font-medium">Prev → Cur (IDR)</th>
                <th className="text-right px-3 py-2 font-medium">Δ Amount</th>
                <th className="text-right px-3 py-2 font-medium">Δ Rate</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-line/40 hover:bg-surface2" data-testid={`${testid}-row-${i}`}>
                  <td className="px-3 py-2 text-xs">{r.merchant}</td>
                  <td className="px-3 py-2 text-right font-mono text-[11px] text-slate-400">{fmtIDR(r.prev_amount)} → <span className="text-white">{fmtIDR(r.cur_amount)}</span></td>
                  <td className={`px-3 py-2 text-right font-mono text-xs ${dirColor}`}>{r.amount_delta > 0 ? "+" : ""}{fmtIDR(r.amount_delta)} <span className="opacity-70">({r.amount_pct > 0 ? "+" : ""}{r.amount_pct.toFixed(1)}%)</span></td>
                  <td className="px-3 py-2 text-right"><Badge color={badgeColor}>{r.rate_delta > 0 ? "+" : ""}{r.rate_delta.toFixed(2)}%</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
